import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FaTimes, FaSave, FaTrash, FaClock, FaCalendarAlt, FaUser, FaTag, FaPlus, FaExternalLinkAlt } from 'react-icons/fa';
import calendarService from '../../services/calendarService';

const QuickCreateModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  selectedDate,
  selectedHour = 9,
  evento = null,
  isEditing = false
}) => {
  const { theme } = useTheme();
  
  const getInitialState = () => ({
    CAL_Titolo: '',
    CAL_Descrizione: '',
    dataOra: '',
    dataFine: '',
    CAL_Id_TipiEventi: 1,
    CAL_Partecipanti: [],
    CAL_Luogo: '',
    CAL_GiornataIntera: false,
    titolo: '',
    descrizione: '',
    tipoEvento: '1',
    partecipanti: [],
    ubicazione: '',
    priorita: 'normale',
    CAL_Id_CalendarioEventi: null,
  });

  // Form state
  const [formData, setFormData] = useState(getInitialState());

  const [availableUsers, setAvailableUsers] = useState([]);
  const [newParticipant, setNewParticipant] = useState('');
  const [eventTypes, setEventTypes] = useState([]);
  const [isLoadingEventTypes, setIsLoadingEventTypes] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Initialize form when modal opens
  useEffect(() => {
    // Modal initialization
    
    if (!isOpen) {
      // Azzeramento completo quando il modal non è visibile
      setFormData(getInitialState());
      setErrors({});
      return;
    }

    // Se il modal è aperto, popola i dati
    if (isEditing && evento) {
      // ✅ FIXED: Inizializza solo se è davvero un evento diverso
      const currentEventId = formData.CAL_Id_CalendarioEventi || formData.eventId;
      const newEventId = evento.CAL_Id_CalendarioEventi || evento.CAL_Id;
      
      if (currentEventId !== newEventId) {
        try {
          // FIXED: Usa CAL_DataOraInizio invece di CAL_DataOra
          const startDate = createSafeDate(evento.CAL_DataOraInizio);
          const endDate = createSafeDate(evento.CAL_DataOraFine);
          
          // ✅ PARSING SICURO DEI PARTECIPANTI
          let partecipanti = [];
          if (evento.CAL_Partecipanti) {
            if (Array.isArray(evento.CAL_Partecipanti)) {
              partecipanti = evento.CAL_Partecipanti;
            } else if (typeof evento.CAL_Partecipanti === 'string') {
              try {
                partecipanti = JSON.parse(evento.CAL_Partecipanti);
                if (!Array.isArray(partecipanti)) partecipanti = [];
              } catch (e) {
                console.warn('Errore parsing partecipanti:', e);
                partecipanti = [];
              }
            }
          }
          
          if (startDate) {
            setFormData({
              // ✅ MAPPING COMPLETO E SICURO
              CAL_Titolo: evento.CAL_Titolo || '',
              CAL_Descrizione: evento.CAL_Descrizione || '',
              CAL_Luogo: evento.CAL_Luogo || '',
              CAL_Id_TipiEventi: evento.CAL_Id_TipiEventi || 1,
              CAL_GiornataIntera: evento.CAL_GiornataIntera || false,
              CAL_Partecipanti: partecipanti,
              CAL_Id_CalendarioEventi: newEventId, // ✅ Traccia l'ID per confronti futuri
              eventId: newEventId, // ✅ Backup ID
              dataOra: toDateTimeLocal(evento.CAL_DataOraInizio),     // ✅ FORMATO DATETIME-LOCAL
              dataFine: toDateTimeLocal(evento.CAL_DataOraFine),     // ✅ FORMATO DATETIME-LOCAL
              // Campi per compatibilità con il form
              titolo: evento.CAL_Titolo || '',
              descrizione: evento.CAL_Descrizione || '',
              ubicazione: evento.CAL_Luogo || '',
              tipoEvento: (evento.CAL_Id_TipiEventi || 1).toString(),
              partecipanti: partecipanti,
              priorita: 'normale'
            });
          } else {
            console.error('Data evento non valida:', evento.CAL_DataOraInizio);
          }
        } catch (error) {
          console.error('Errore inizializzazione form evento:', error);
        }
      }
    } else {
      // ✅ FIX: Resetta SEMPRE il form per nuovi eventi
      let defaultStart;
      
      if (selectedDate) {
        defaultStart = new Date(selectedDate);
        defaultStart.setHours(selectedHour || 9, 0, 0, 0);
      } else {
        defaultStart = new Date();
        if (selectedHour !== undefined && selectedHour !== null) {
          defaultStart.setHours(selectedHour, 0, 0, 0);
        }
      }
      
      const defaultEnd = new Date(defaultStart.getTime() + 60 * 60 * 1000);
      
      console.log(`📅 Nuovo evento: data=${selectedDate}, ora=${selectedHour}, start=${defaultStart}, end=${defaultEnd}`);
      
      setFormData({
        CAL_Titolo: '',
        CAL_Descrizione: '',
        CAL_Partecipanti: [],
        CAL_Id_CalendarioEventi: null, // ✅ Resetta ID
        eventId: null, // ✅ Resetta backup ID
        dataOra: toDateTimeLocal(toMySQLDateTime(defaultStart)),
        dataFine: toDateTimeLocal(toMySQLDateTime(defaultEnd)),
        CAL_Luogo: '',
        CAL_GiornataIntera: false,
        CAL_Id_TipiEventi: 1,
        titolo: '',
        descrizione: '',
        tipoEvento: '1',
        partecipanti: [],
        ubicazione: '',
        priorita: 'normale'
      });
    }
  }, [isOpen, isEditing, evento, selectedDate, selectedHour]);

  // Load available users
  useEffect(() => {
    const loadAvailableUsers = async () => {
      try {
        const users = await calendarService.getAvailableUsers();
        setAvailableUsers(users);
      } catch (error) {
        console.error('Errore caricamento utenti:', error);
      }
    };

    if (isOpen) {
      loadAvailableUsers();
    }
  }, [isOpen]);

  // ✅ NUOVO: Load event types tramite API
  useEffect(() => {
    const loadEventTypes = async () => {
      setIsLoadingEventTypes(true);
      try {
        const types = await calendarService.getEventTypes();
        setEventTypes(types);
      } catch (error) {
        console.error('Errore caricamento tipi evento:', error);
        
        // Fallback: usa tipi predefiniti se API fallisce
        setEventTypes([
          { id: 1, name: 'Sede Arezzo', description: 'Sede Arezzo', code: 'SEDE', color: '#6B7280', isActive: true },
          { id: 2, name: 'Ferie', description: 'Ferie', code: 'FERIE', color: '#F59E0B', isActive: true },
          { id: 3, name: 'Permessi', description: 'Permessi', code: 'PERMESSI', color: '#8B5CF6', isActive: true },
          { id: 4, name: 'Web Meeting', description: 'Web Meeting', code: 'WEBMEETING', color: '#3B82F6', isActive: true },
          { id: 5, name: 'Riunione', description: 'Riunione', code: 'RIUNIONE', color: '#10B981', isActive: true }
        ]);
      } finally {
        setIsLoadingEventTypes(false);
      }
    };

    if (isOpen) {
      loadEventTypes();
    }
  }, [isOpen]);

  // ✅ NEW: Gestione automatica partecipanti in base al tipo evento
  useEffect(() => {
    if (!isOpen || availableUsers.length === 0 || eventTypes.length === 0) return;
    
    const tipoEvento = formData.tipoEvento || formData.CAL_Id_TipiEventi || '1';
    const isFerieOrPermessi = isFerieOrPermessiEventType(tipoEvento);
    
    if (isFerieOrPermessi) {
      // Per Ferie e Permessi: nessun partecipante selezionato di default (deve scegliere 1)
      if (!isEditing) {
        setFormData(prev => ({
          ...prev,
          partecipanti: [],
          CAL_Partecipanti: []
        }));
      }
    } else {
      // Per altri tipi evento: tutti gli utenti selezionati di default
      if (!isEditing) {
        const allUserIds = availableUsers.map(user => user.UTE_Id_Utenti.toString());
        setFormData(prev => ({
          ...prev,
          partecipanti: allUserIds,
          CAL_Partecipanti: allUserIds
        }));
      }
    }
  }, [formData.tipoEvento, formData.CAL_Id_TipiEventi, availableUsers, isOpen, isEditing]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // ✅ NEW: Gestione speciale per cambio tipo evento
    if (field === 'tipoEvento' && availableUsers.length > 0) {
      const isFerieOrPermessi = value === '2' || value === '3' || value === 2 || value === 3;
      
      if (isFerieOrPermessi) {
        // Per Ferie e Permessi: reset partecipanti (deve scegliere 1)
        setFormData(prev => ({
          ...prev,
          [field]: value,
          partecipanti: [],
          CAL_Partecipanti: []
        }));
      } else {
        // Per altri tipi: seleziona tutti
        const allUserIds = availableUsers.map(user => user.UTE_Id_Utenti.toString());
        setFormData(prev => ({
          ...prev,
          [field]: value,
          partecipanti: allUserIds,
          CAL_Partecipanti: allUserIds
        }));
      }
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // ✅ FIX: Funzione per convertire da MySQL a datetime-local
  const toDateTimeLocal = (mysqlDateTime) => {
    if (!mysqlDateTime) return '';
    
    try {
      // "2025-08-04 14:31:00" → "2025-08-04T14:31"
      const date = new Date(mysqlDateTime.replace(' ', 'T'));
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.warn('Errore conversione data:', mysqlDateTime, error);
      return '';
    }
  };

  // ✅ FIX: Funzione per convertire da datetime-local a MySQL
  const fromDateTimeLocalToMySQL = (datetimeLocal) => {
    if (!datetimeLocal) return '';
    
    try {
      // "2025-08-04T14:31" → "2025-08-04 14:31:00"
      const date = new Date(datetimeLocal);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.warn('Errore conversione datetime-local:', datetimeLocal, error);
      return '';
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // ✅ CONTROLLO SICURO DEL TITOLO
    const titolo = formData.titolo || formData.CAL_Titolo || '';
    if (!titolo.trim()) {
      newErrors.titolo = 'Il titolo è obbligatorio';
    }
    
    if (!formData.dataOra) {
      newErrors.dataOra = 'Data e ora di inizio sono obbligatorie';
    }
    
    if (formData.dataFine && formData.dataOra) {
      // ✅ CONVERTI ENTRAMBE PER CONFRONTO
      const startMySQL = fromDateTimeLocalToMySQL(formData.dataOra);
      const endMySQL = fromDateTimeLocalToMySQL(formData.dataFine);
      
      const start = new Date(startMySQL.replace(' ', 'T'));
      const end = new Date(endMySQL.replace(' ', 'T'));
      
      if (end <= start) {
        newErrors.dataFine = 'La data di fine deve essere successiva all\'inizio';
      }
    }
    
    // ✅ NEW: Validazione partecipanti per Ferie e Permessi (aggiornata)
    const tipoEvento = formData.tipoEvento || formData.CAL_Id_TipiEventi || '1';
    const isFerie = isFerieEventType(tipoEvento);
    const isPermessi = isPermessiEventType(tipoEvento);
    const partecipanti = formData.partecipanti || [];
    const isGiornataIntera = formData.giornataIntera || formData.CAL_GiornataIntera;
    
    if (isFerie) {
      // ✅ CHANGE: Per FERIE, permettiamo sempre 0 partecipanti (con conferma) o 1 (normale)
      if (partecipanti.length > 1) {
        newErrors.partecipanti = 'Per Ferie è possibile selezionare massimo un utente';
      }
      // NON blocchiamo più il caso 0 partecipanti - sarà gestito dalla modale di conferma
    } else if (isPermessi) {
      // Per PERMESSI: sempre obbligatorio selezionare esattamente 1 utente
      if (partecipanti.length === 0) {
        newErrors.partecipanti = 'Per Permessi è obbligatorio selezionare esattamente un utente';
      } else if (partecipanti.length > 1) {
        newErrors.partecipanti = 'Per Permessi è possibile selezionare solo un utente';
      }
    } else if (isMalattiaEventType(tipoEvento)) {
      // Per MALATTIA: obbligatorio selezionare esattamente 1 utente
      if (partecipanti.length === 0) {
        newErrors.partecipanti = 'Per Malattia è obbligatorio selezionare esattamente un utente';
      } else if (partecipanti.length > 1) {
        newErrors.partecipanti = 'Per Malattia è possibile selezionare solo un utente';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    console.log('🔍 DEBUG handleSave - Dati form:', {
      tipoEvento: formData.tipoEvento || formData.CAL_Id_TipiEventi,
      giornataIntera: formData.giornataIntera,
      CAL_GiornataIntera: formData.CAL_GiornataIntera,
      partecipanti: formData.partecipanti
    });
    
    if (!validateForm()) {
      return;
    }
    
    // ✅ NEW: Controllo per ferie giornata intera senza partecipanti
    const tipoEvento = formData.tipoEvento || formData.CAL_Id_TipiEventi || '1';
    const isFerie = isFerieEventType(tipoEvento);
    const partecipanti = formData.partecipanti || [];
    const isGiornataIntera = formData.giornataIntera || formData.CAL_GiornataIntera;
    
    if (isFerie && partecipanti.length === 0) {
      // ✅ CHANGE: Mostra modale di conferma per ferie senza partecipanti (indipendentemente da giornata intera)
      setShowConfirmModal(true);
      return;
    }
    
    // Procedi con il salvataggio normale
    await performSave();
  };

  // ✅ NEW: Funzione separata per eseguire il salvataggio
  const performSave = async () => {
    setIsLoading(true);
    try {
      const eventData = {
        CAL_Titolo: formData.titolo || formData.CAL_Titolo || '',
        CAL_DataOraInizio: fromDateTimeLocalToMySQL(formData.dataOra),      // ✅ CONVERTI A MYSQL
        CAL_DataOraFine: fromDateTimeLocalToMySQL(formData.dataFine),       // ✅ CONVERTI A MYSQL
        CAL_Descrizione: formData.descrizione || formData.CAL_Descrizione || '',
        CAL_Luogo: formData.ubicazione || formData.CAL_Luogo || '',
        CAL_Id_TipiEventi: parseInt(formData.tipoEvento || formData.CAL_Id_TipiEventi) || 1,
        CAL_Partecipanti: formData.partecipanti || formData.CAL_Partecipanti || [],
        CAL_GiornataIntera: formData.giornataIntera || formData.CAL_GiornataIntera ? 1 : 0
      };
      
      console.log('📅 Dati evento inviati (formato MySQL):', eventData);
      await onSave(eventData);
    } catch (error) {
      console.error('Errore salvataggio evento:', error);
      setErrors({ general: 'Errore durante il salvataggio' });
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ NEW: Gestione conferma ferie generali
  const handleConfirmGeneralHoliday = async () => {
    setShowConfirmModal(false);
    await performSave();
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!isEditing || !evento) return;
    if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
      setIsLoading(true);
      try {
        const eventId = evento.CAL_Id_CalendarioEventi || evento.CAL_Id;
        if (!eventId) throw new Error('ID evento non trovato');
        await onDelete?.(eventId);
        onClose?.(); // chiudi modale dopo eliminazione
      } catch (error) {
        console.error('Errore eliminazione evento:', error);
        setErrors({ general: 'Errore durante l\'eliminazione' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // ✅ AGGIORNATO: Get event type name dall'API
  const getEventTypeName = (typeId) => {
    const eventType = eventTypes.find(type => type.id === parseInt(typeId));
    if (eventType) {
      return eventType.name || eventType.description;
    }
    // Fallback per tipi non trovati
    return `Tipo ${typeId}`;
  };

  // ✅ NUOVO: Helper functions per identificare tipi evento dinamicamente
  const getEventTypeByCode = (code) => {
    return eventTypes.find(type => type.code === code);
  };

  const isFerieEventType = (typeId) => {
    const eventType = eventTypes.find(type => type.id === parseInt(typeId));
    return eventType?.code === 'FERIE';
  };

  const isPermessiEventType = (typeId) => {
    const eventType = eventTypes.find(type => type.id === parseInt(typeId));
    return eventType?.code === 'PERMESSI';
  };

  const isMalattiaEventType = (typeId) => {
    const eventType = eventTypes.find(type => type.id === parseInt(typeId));
    return eventType?.code === 'MALATTIA';
  };

  const isFerieOrPermessiEventType = (typeId) => {
    return isFerieEventType(typeId) || isPermessiEventType(typeId) || isMalattiaEventType(typeId);
  };

  // Helper per date sicure
  const createSafeDate = (dateValue) => {
    if (!dateValue) return null;
    
    try {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.warn('Errore parsing data:', dateValue, error);
      return null;
    }
  };

  // ✅ NUOVA FUNZIONE: Converte Date in formato MySQL
  const toMySQLDateTime = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return null;
    }
    
    // Formato MySQL: "2025-08-04 14:31:00"
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Manage participants
  const addParticipant = (userId) => {
    const currentParticipants = formData.partecipanti || [];
    if (!currentParticipants.includes(userId.toString())) {
      const newParticipants = [...currentParticipants, userId.toString()];
      setFormData(prev => ({
        ...prev,
        partecipanti: newParticipants,
        CAL_Partecipanti: newParticipants
      }));
    }
  };

  const removeParticipant = (userId) => {
    const currentParticipants = formData.partecipanti || [];
    const newParticipants = currentParticipants.filter(id => id !== userId.toString());
    setFormData(prev => ({
      ...prev,
      partecipanti: newParticipants,
      CAL_Partecipanti: newParticipants
    }));
  };

  const addExternalParticipant = () => {
    const trimmed = newParticipant.trim();
    const currentParticipants = formData.partecipanti || [];
    if (trimmed && !currentParticipants.includes(trimmed)) {
      const newParticipants = [...currentParticipants, trimmed];
      setFormData(prev => ({
        ...prev,
        partecipanti: newParticipants,
        CAL_Partecipanti: newParticipants
      }));
      setNewParticipant('');
    }
  };

  // ✅ NUOVO: Toggle partecipante con checkbox
  const toggleParticipant = (userId) => {
    const currentParticipants = formData.partecipanti || [];
    const userIdString = userId.toString();
    
    let newParticipants;
    if (currentParticipants.includes(userIdString)) {
      // Rimuovi
      newParticipants = currentParticipants.filter(id => id !== userIdString);
    } else {
      // Aggiungi
      newParticipants = [...currentParticipants, userIdString];
    }
    
    setFormData(prev => ({
      ...prev,
      partecipanti: newParticipants,
      CAL_Partecipanti: newParticipants
    }));
  };

  const getParticipantLabel = (participantId) => {
    // Se è un numero, cerca nell'elenco utenti
    const user = availableUsers.find(u => u.UTE_Id_Utenti.toString() === participantId);
    if (user) {
      return user.UTE_NomeUtente;
    }
    // Altrimenti è un partecipante esterno
    return participantId;
  };

  // ✅ NEW: Helper functions per gestione link
  const isValidUrl = (string) => {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const openLinkInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  // Theme classes
  const modalBg = theme === 'dark' ? 'bg-gray-900/95' : 'bg-black/50';
  const cardBg = theme === 'dark' 
    ? 'bg-gray-800 border-indigo-700/50' 
    : 'bg-white border-emerald-400/50';
  
  const inputClasses = theme === 'dark' 
    ? 'border-gray-600 bg-gray-700/50 text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' 
    : 'border-gray-300 bg-white text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500';
  
  const buttonPrimaryClasses = theme === 'dark' 
    ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white' 
    : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white';
  
  const buttonSecondaryClasses = theme === 'dark'
    ? 'bg-gray-600 hover:bg-gray-500 text-gray-100 border border-gray-500'
    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300';

  const textColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';
  const titleColor = theme === 'dark' ? 'text-indigo-200' : 'text-emerald-700';

  return (
    <>
      <div className={`fixed inset-0 z-50 ${modalBg} backdrop-blur-sm flex items-center justify-center p-4`}>
      <div className={`${cardBg} rounded-lg border shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className={`text-xl font-bold ${titleColor} flex items-center`}>
            <FaCalendarAlt className="mr-2" />
            {isEditing ? 'Modifica Evento' : 'Nuovo Evento'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FaTimes className={textColor} />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {errors.general && (
            <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              {errors.general}
            </div>
          )}

          {/* Layout 2 colonne su desktop: campi a sinistra, partecipanti a destra */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4">
          <div className="space-y-4">

          {/* Titolo */}
          <div>
            <label className={`block text-sm font-medium ${textColor} mb-2`}>
              <FaTag className="inline mr-2" />
              Titolo *
            </label>
            <input
              type="text"
              value={formData.titolo || ''}
              onChange={(e) => handleInputChange('titolo', e.target.value)}
              className={`w-full p-3 rounded-lg border ${inputClasses} ${errors.titolo ? 'border-red-500' : ''}`}
              placeholder="Inserisci il titolo dell'evento"
              disabled={isLoading}
            />
            {errors.titolo && <p className="text-red-500 text-xs mt-1">{errors.titolo}</p>}
          </div>

          {/* Date e orari */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                <FaClock className="inline mr-2" />
                Inizio *
              </label>
              <input
                type="datetime-local"
                value={formData.dataOra}
                onChange={(e) => handleInputChange('dataOra', e.target.value)}
                className={`w-full p-3 rounded-lg border ${inputClasses} ${errors.dataOra ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
              {errors.dataOra && <p className="text-red-500 text-xs mt-1">{errors.dataOra}</p>}
            </div>

            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                <FaClock className="inline mr-2" />
                Fine
              </label>
              <input
                type="datetime-local"
                value={formData.dataFine}
                onChange={(e) => handleInputChange('dataFine', e.target.value)}
                className={`w-full p-3 rounded-lg border ${inputClasses} ${errors.dataFine ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
              {errors.dataFine && <p className="text-red-500 text-xs mt-1">{errors.dataFine}</p>}
            </div>
          </div>

          {/* ✅ AGGIUNTO: Checkbox Giornata Intera */}
          <div>
            <label className={`flex items-center gap-3 cursor-pointer`}>
              <input
                type="checkbox"
                checked={formData.giornataIntera || formData.CAL_GiornataIntera || false}
                onChange={(e) => handleInputChange('giornataIntera', e.target.checked)}
                className="w-4 h-4 text-emerald-600 bg-white border-gray-300 rounded focus:ring-emerald-500 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                disabled={isLoading}
              />
              <div>
                <span className={`text-sm font-medium ${textColor}`}>
                  Giornata intera
                </span>
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  L'evento dura tutto il giorno
                </p>
              </div>
            </label>
          </div>

          {/* Tipo evento + Ubicazione - layout 2 colonne su desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                Tipo Evento
              </label>
              <select
                value={formData.tipoEvento}
                onChange={(e) => handleInputChange('tipoEvento', e.target.value)}
                className={`w-full p-3 rounded-lg border ${inputClasses}`}
                disabled={isLoading || isLoadingEventTypes}
              >
                {isLoadingEventTypes ? (
                  <option value="">Caricamento tipi evento...</option>
                ) : (
                  eventTypes
                    .filter(type => type.isActive !== false) // Filtra solo i tipi attivi
                    .map(type => (
                      <option key={type.id} value={type.id.toString()}>
                        {type.name || type.description}
                      </option>
                    ))
                )}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                Ubicazione
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.ubicazione || ''}
                  onChange={(e) => handleInputChange('ubicazione', e.target.value)}
                  className={`w-full p-3 rounded-lg border ${inputClasses} ${isValidUrl(formData.ubicazione || '') ? 'pr-12' : ''}`}
                  placeholder="Luogo dell'evento (opzionale)"
                  disabled={isLoading}
                />
                {/* ✅ NEW: Pulsante link se il testo è un URL valido */}
                {isValidUrl(formData.ubicazione || '') && (
                  <button
                    type="button"
                    onClick={() => openLinkInNewTab(formData.ubicazione)}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-lg transition-colors ${
                      theme === 'dark' 
                        ? 'text-indigo-400 hover:text-indigo-300 hover:bg-indigo-900/20' 
                        : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                    }`}
                    title="Apri link in nuova scheda"
                  >
                    <FaExternalLinkAlt className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Descrizione */}
          <div>
            <label className={`block text-sm font-medium ${textColor} mb-2`}>
              Descrizione
            </label>
            <textarea
              value={formData.descrizione || ''}
              onChange={(e) => handleInputChange('descrizione', e.target.value)}
              rows="3"
              className={`w-full p-3 rounded-lg border ${inputClasses}`}
              placeholder="Descrizione dell'evento (opzionale)"
              disabled={isLoading}
            />
          </div>

          </div>
          {/* Colonna destra: Partecipanti */}
          <div>

          {/* Partecipanti - NUOVA UI CON CHECKBOX */}
          <div>
            <label className={`block text-sm font-medium ${textColor} mb-2`}>
              <FaUser className="inline mr-2" />
              Partecipanti
            </label>
            
            {/* ✅ NEW: Suggerimenti dinamici in base al tipo evento */}
            {(() => {
              const tipoEvento = formData.tipoEvento || formData.CAL_Id_TipiEventi || '1';
              const isFerieOrPermessi = isFerieOrPermessiEventType(tipoEvento);
              const partecipanti = formData.partecipanti || [];
              
              if (isFerieOrPermessi) {
                const isFerie = isFerieEventType(tipoEvento);
                return (
                  <div className="text-xs text-amber-600 dark:text-amber-400 mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
                    📝 Per <strong>{isFerie ? 'Ferie' : 'Permessi'}</strong>: selezionare esattamente <strong>un utente</strong>
                    {partecipanti.length === 0 && ' (nessuno selezionato)'}
                    {partecipanti.length > 1 && ` (${partecipanti.length} selezionati - troppi!)`}
                  </div>
                );
              } else {
                return (
                  <div className="text-xs text-blue-600 dark:text-blue-400 mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                    💡 Tutti gli utenti sono selezionati di default. <strong>Deseleziona</strong> quelli che non parteciperanno all'evento.
                  </div>
                );
              }
            })()}
            
            {/* Grid di utenti con checkbox */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 max-h-40 lg:max-h-[50vh] overflow-y-auto">
              {availableUsers.map(user => {
                const isSelected = (formData.partecipanti || []).includes(user.UTE_Id_Utenti.toString());
                return (
                  <div
                    key={user.UTE_Id_Utenti}
                    onClick={() => toggleParticipant(user.UTE_Id_Utenti)}
                    className={`flex items-center p-2 rounded-lg border cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? theme === 'dark' 
                          ? 'bg-indigo-600/20 border-indigo-500 text-indigo-300' 
                          : 'bg-emerald-50 border-emerald-500 text-emerald-700'
                        : theme === 'dark'
                          ? 'bg-gray-700/50 border-gray-600 text-gray-300 hover:bg-gray-600/50'
                          : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded border-2 mr-3 flex items-center justify-center ${
                      isSelected 
                        ? theme === 'dark' 
                          ? 'bg-indigo-600 border-indigo-600' 
                          : 'bg-emerald-600 border-emerald-600'
                        : 'border-gray-400'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{user.UTE_NomeUtente}</div>
                      {user.UTE_Email && (
                        <div className="text-xs opacity-70">{user.UTE_Email}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Partecipanti esterni */}
            <div className="border-t pt-3">
              <label className={`block text-xs ${textColor} mb-2`}>
                Aggiungi partecipante esterno:
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newParticipant}
                  onChange={(e) => setNewParticipant(e.target.value)}
                  placeholder="Nome o email partecipante esterno"
                  className={`flex-1 p-2 rounded-lg border text-sm ${inputClasses}`}
                  disabled={isLoading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addExternalParticipant();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={addExternalParticipant}
                  disabled={!newParticipant.trim() || isLoading}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${buttonSecondaryClasses} disabled:opacity-50`}
                >
                  <FaPlus />
                </button>
              </div>
              
              {/* Lista partecipanti esterni */}
              {(formData.partecipanti || []).filter(p => isNaN(parseInt(p))).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {(formData.partecipanti || [])
                    .filter(p => isNaN(parseInt(p)))
                    .map((participantId, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                          theme === 'dark' 
                            ? 'bg-gray-600 text-gray-200' 
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {participantId}
                        <button
                          type="button"
                          onClick={() => removeParticipant(participantId)}
                          className="ml-1 text-xs hover:text-red-500"
                        >
                          <FaTimes />
                        </button>
                      </span>
                    ))
                  }
                </div>
              )}
            </div>
            
            {/* Errore partecipanti */}
            {errors.partecipanti && (
              <p className="text-red-500 text-xs mt-1">{errors.partecipanti}</p>
            )}
          </div>

          </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div>
            {isEditing && (
              <button
                onClick={handleDelete}
                disabled={isLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
              >
                <FaTrash className="h-4 w-4" />
                <span>Elimina</span>
              </button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${buttonSecondaryClasses} disabled:opacity-50`}
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${buttonPrimaryClasses} flex items-center space-x-2 disabled:opacity-50`}
            >
              <FaSave className="h-4 w-4" />
              <span>{isLoading ? 'Salvataggio...' : 'Salva'}</span>
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* ✅ NEW: Modale di conferma per ferie generali */}
      {showConfirmModal && (
        <div className={`fixed inset-0 ${modalBg} backdrop-blur-sm flex items-center justify-center p-4`} style={{ zIndex: 9999 }}>
          <div className={`${cardBg} rounded-lg border shadow-xl w-full max-w-md`}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className={`text-lg font-bold ${titleColor} flex items-center`}>
                <FaCalendarAlt className="mr-2" />
                Conferma Ferie Generali
              </h3>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <p className={`${textColor} mb-3`}>
                  Stai creando un evento di ferie a giornata intera senza selezionare alcun utente specifico.
                </p>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <p className="text-amber-800 dark:text-amber-200 text-sm">
                    <strong>⚠️ Conferma:</strong> È obbligatorio segnare il proprio utente a meno che non si tratti di una giornata di ferie generale (es: festività nazionale).
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelConfirm}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${buttonSecondaryClasses}`}
                >
                  Annulla e Modifica
                </button>
                <button
                  onClick={handleConfirmGeneralHoliday}
                  disabled={isLoading}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${buttonPrimaryClasses} disabled:opacity-50`}
                >
                  {isLoading ? 'Creazione...' : 'Conferma Ferie Generali'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickCreateModal;
